import { Form } from 'antd';
import { AppDispatch } from 'models/app-dispatch';
import { Organization } from 'models/organization';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Invitation, InvitationAction } from 'services/auth.service';
import { GetOrganization } from 'services/me.service';
import { OrganizationUpdateDto, OrganizationCreateDto, Update, Create, GetAll, Export } from 'services/organization.service';
import { GetMe } from 'redux/actions/me';
import { Api } from 'services/api';
import _ from 'lodash';
import moment from 'moment';

export function mapOrganization(item: Organization) {
  return {
    ...item,
    entityName: item.name,
    entityType: item.organizationType,
    createdAt: moment(item.createdAt),
    trialEndDate: moment(item.trialEndDate),
  };
}

export function useOrganizationInvitation() {
  // TODO: Review this
  const dispatch = useDispatch<AppDispatch>();

  const [isSaving, setIsSaving] = useState(false);

  const accept = async (code: string) => {
    return await handle(code, InvitationAction.Accept);
  };

  const decline = async (code: string) => {
    return await handle(code, InvitationAction.Decline);
  };

  const handle = async (code: string, action: InvitationAction) => {
    setIsSaving(true);
    try {
      await Invitation({ code, action });
      dispatch(GetMe());
    } finally {
      setIsSaving(false);
    }
  };

  return { isSaving, accept, decline };
}

export function useSaveOrganisation() {
  const [isSaving, setIsSaving] = useState(false);
  const [form] = Form.useForm();

  const update = async (id: string, data: OrganizationUpdateDto) => {
    setIsSaving(true);
    try {
      return await Update(id, data);
    } finally {
      setIsSaving(false);
    }
  };

  const create = async (data: OrganizationCreateDto) => {
    setIsSaving(true);
    try {
      return await Create(data);
    } finally {
      setIsSaving(false);
    }
  };

  return { isSaving, update, create, form };
}

export function useOrganisation() {
  const [loading, setIsLoading] = useState(false);

  const load = async () => {
    setIsLoading(true);
    try {
      const obj = await GetOrganization();
      return mapOrganization(obj);
    } finally {
      setIsLoading(false);
    }
  };

  return { loading, load };
}

interface OrganizationDefaultFilters {
  search?: string;
}

export const defaultUsersFilters: OrganizationDefaultFilters = {
  search: '',
};

interface FetchOrganizationsProps {
  defaultFilters?: OrganizationDefaultFilters;
  pageNumber?: number;
  pageSize?: number;
  text?: string;
}

export function useOrganizations(props: FetchOrganizationsProps) {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>();
  const [data, setData] = useState<Organization[]>();
  const [total, setTotal] = useState<number>();
  const [pagination, setPagination] = useState({ pageSize: 100, pageNumber: 1 });
  const [filterCounter, setFilterCounter] = useState(0);
  const [filter, setFilter] = useState<OrganizationDefaultFilters>({
    search: props.defaultFilters?.search ?? defaultUsersFilters.search,
  });

  const load = async () => {
    const filters = {
      pageNumber: props.pageNumber ?? pagination.pageNumber,
      pageSize: props.pageSize ?? pagination.pageSize,
      text: props.text ?? filter.search,
    };

    const diffSearch = !_.isEqual(defaultUsersFilters.search, filters.text);

    const counter = [diffSearch].filter(Boolean).length;

    setFilterCounter(counter);

    setLoading(true);
    await Api.tryCatch(
      GetAll(filters),
      (rs) => {
        setLoading(false);
        setData(rs.data.map(mapOrganization));
        setTotal(rs.meta.total);
      },
      (err) => {
        setLoading(false);
        setError(err.message);
      },
    );
  };

  let cancelToken = Api.getCancelTokenSource();
  useEffect(() => {
    cancelToken = Api.getCancelTokenSource();
    return () => {
      cancelToken.cancel();
    };
  }, []);

  useEffect(() => {
    load();
  }, [props.pageNumber, props.pageSize, props.text, filter, pagination]);

  return { loading, error, data, total, pagination, setPagination, filterCounter, setFilter, reloadList: load };
}

export function useExportOrganisation() {
  const [isExporting, setIsExporting] = useState(false);

  const exportOrg = async () => {
    setIsExporting(true);
    try {
      return await Export();
    } finally {
      setIsExporting(false);
    }
  };

  return { isExporting, exportOrg };
}